import { useEffect, useState } from 'react';

import ShippingProfile from '@/shared/types/shipping/shippingProfile.type';
import Zone from '@/shared/types/shipping/zones/zone.type';

import useAssignStates, {
  Checkbox,
} from '../components/AssignStates/hooks/useAssignStates';

type Args = {
  countries: {
    country: string;
    code: string;
    provinces: { name: string; code: string }[];
  }[];
  shippingProfile: ShippingProfile;
  zone?: Zone;
  zoneKey?: keyof Zone;
};

const useAddZoneDialog = ({
  countries,
  shippingProfile,
  zone,
  zoneKey,
}: Args) => {
  const [profile, setProfile] = useState<ShippingProfile>(shippingProfile);

  const [form, setForm] = useState({
    delivery_country: '',
    delivery_city: '',
    shipping_rate_type: '',
    shipping_rate: '',
  });

  const [formErrors, setFormErrors] = useState<
    Partial<typeof form & { deliver_state: string }>
  >({});

  const [availableStates, setAvailableStates] = useState<Checkbox[]>([]);

  const assignStatesState = useAssignStates({
    availableStates,
  });

  useEffect(() => {
    const getCountriesOptions = () => {
      const listCountries = countries.find(
        (country) => country.code === form.delivery_country,
      );

      if (zoneKey && ['delivery_state', 'delivery_city'].includes(zoneKey)) {
        return (
          listCountries?.provinces.map((province) => ({
            label: province.name,
            value: province.code,
            checked: zone?.delivery_state === province.code,
          })) || []
        );
      }

      return (
        listCountries?.provinces.map((province) => ({
          label: province.name,
          value: province.code,
        })) || []
      );
    };

    setAvailableStates(getCountriesOptions());
  }, [countries, form.delivery_country, zone?.delivery_state, zoneKey]);

  useEffect(() => {
    if (zone && zoneKey) {
      if (['delivery_country', 'delivery_state'].includes(zoneKey)) {
        setForm((prev) => ({
          ...prev,
          delivery_country: zone.delivery_country,
          delivery_city: zone?.delivery_city ?? '',
        }));
      }
    }
  }, [zone, zoneKey]);

  useEffect(() => {
    setProfile(shippingProfile);
  }, [shippingProfile]);

  const onValidate = () => {
    const errors: typeof formErrors = {};

    if (!form.delivery_country) {
      errors.delivery_country = 'Field is Required';
    }

    if (assignStatesState.assigned.length === 0) {
      errors.deliver_state = 'At least one state should be selected';
    } else if (profile.zones.length > 0) {
      const shippingProfileZones = profile.zones.filter((zone) =>
        assignStatesState.assigned.some(
          (item) =>
            item.value === zone.delivery_state &&
            form.delivery_country === zone.delivery_country,
        ),
      );

      const assignedCity = shippingProfileZones.find(
        (zone) => zone.delivery_city === form.delivery_city,
      );

      if (shippingProfileZones.length > 0 && assignedCity) {
        assignStatesState.setAssigned((prev) =>
          prev.map((assigned) =>
            shippingProfileZones.find(
              (zone) => assigned.value === zone.delivery_state,
            )
              ? {
                  ...assigned,
                  error:
                    assignStatesState.assigned.length === 1
                      ? `${assigned.label} has already been assigned to the current shipping profile. Please unassign this state or select a different city.`
                      : `${assigned.label} has already been assigned to the current shipping profile. Please select a different state.`,
                }
              : assigned,
          ),
        );

        errors.deliver_state = ' ';
      }
    }

    if (!form.shipping_rate_type) {
      errors.shipping_rate_type = 'Field is Required';
    }

    if (!form.shipping_rate) {
      errors.shipping_rate = 'Field is Required';
    }

    if (zoneKey === 'shipping_rate') {
      delete errors.delivery_country;
      delete errors.deliver_state;

      setFormErrors(errors);
      return Object.keys(errors).length === 0;
    } else {
      setFormErrors(errors);
      return Object.keys(errors).length === 0;
    }
  };

  return {
    form,
    formErrors,
    assignStatesState,
    setForm,
    onValidate,
  };
};

export default useAddZoneDialog;
